import { Box, CircularProgress, styled, Typography, Button, ButtonGroup, Alert } from "@mui/material";
import { Profile } from "./types";
import { LoaderFunctionArgs, useLoaderData, useNavigation } from "react-router-dom";
import { RuleSetMetadataEntry } from "../../types/ruleSetTypes";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { GeographicArea, getCompaniesInArea } from 'org-graph/geographicSelection';
import { useAuth } from "react-oidc-context";
import '../../components/AccordionStyles.css';
import { HiddenStateStack } from 'org-graph/hiddenStateStack';
import api, { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import CompanyDetailsPanel from "./CompanyDetailsPanel";
import CompanyMapPanel from "./CompanyMapPanel";
import OrgGraphPanel from "./OrgGraphPanel";
import {
  enableGraphMonitoring,
  fetchGraphMonitoringDetail,
  unsubscribeGraphMonitoring,
} from "../GraphMonitoring/monitoringApi";
import { GraphMonitoringEntityDetail } from "../GraphMonitoring/types";
import { getTenantIdFromAccessToken, isCarEntityListTenant, isIvoLicensesTenant, isTenantCaseTenant } from "../../auth/authUtils";
import { useTranslation } from 'react-i18next';
import appI18n from '../../i18n/i18n';

type PanelKey = 'left' | 'middle' | 'right';

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return target.isContentEditable || Boolean(target.closest('input, textarea, [contenteditable="true"]'));
}

function preloadFonts() {
  const fonts = Array.from(document.fonts).filter(font =>
    (font.family === "Font Awesome 6 Brands" && font.weight === "400") ||
    (font.family === "Font Awesome 6 Free" && font.weight === "900"));

  return Promise.all(fonts.map(font => font.load()));
}

export async function companyAssessmentLoader({ params, request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const caseId = url.searchParams.get('caseId');

  // Preload graph fonts while we wait for the assessment data
  const fontPromise = preloadFonts();

  try {
    const profilesResponse = await api.post<{ profiles: Profile[]; contextId: string }>(
      '/v1/companyprofiles',
      {
        orgNumbers: [params.orgNumber],
        createContext: !caseId,
        caseId: caseId || undefined,
      },
    );

    await fontPromise;

    const profile = profilesResponse.profiles[0];

    // Deserialize hiddenStateStack if it exists in the profile
    if (profile.hiddenStateStack) {
      try {
        profile.hiddenStateStack = HiddenStateStack.fromJSON(profile.hiddenStateStack);
      } catch (error) {
        console.warn('Failed to deserialize hiddenStateStack, using default:', error);
        profile.hiddenStateStack = undefined;
      }
    }

    const profileWithContext = {
      contextId: profilesResponse.contextId,
      ...profile,
    };

    return [200, '', profileWithContext];
  } catch (err) {
    await fontPromise;
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
      return [err.status, typeof err.body === 'string' ? err.body : JSON.stringify(err.body), null, null];
    }
    throw err;
  }
}





// function RuleSetEvaluationCard({ evaluation, metadata }: { evaluation: RuleSetEvaluation, metadata: RuleSetMetadataEntry[] }) {
//   return (
//     <CustomAccordion defaultExpanded={true} className="custom-accordion">
//       <AccordionSummary expandIcon={<ExpandMoreIcon />} className="custom-accordion-summary">
//         <Typography variant="h6" className="custom-accordion-summary">Rule Evaluation</Typography>
//       </AccordionSummary>
//       <AccordionDetails>
//         <Box sx={{ width: '100%' }}>
//           <RuleSetEvaluationDisplay evaluation={evaluation} metadata={metadata}
//           />
//         </Box>
//       </AccordionDetails>
//     </CustomAccordion>
//   )
// }









const Resizer = styled('div')(({ theme }) => ({
  width: '8px',
  cursor: 'col-resize',
  backgroundColor: theme.palette.divider,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
  zIndex: 1,
  flexShrink: 0,
}));

function getMonitoringErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (typeof error.body === 'string' && error.body.trim()) {
      return error.body;
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return appI18n.t('monitoring.overview.loadFailed');
}

export default function CompanyProfileDisplay() {
  const { t } = useTranslation();
  const [httpStatus, errorMessage, companyAssessmentData] = useLoaderData() as [number, string, Profile, RuleSetMetadataEntry[]];
  const navigation = useNavigation();
  const auth = useAuth();
  const toast = useToast();
  const [hiddenNodeIds, setHiddenNodeIds] = useState<Set<string>>(new Set());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [geographicArea, setGeographicArea] = useState<GeographicArea | null>(null);
  
  // Layout state
  const [layout, setLayout] = useState({
    left: 25,
    middle: 75,
    right: 0,
    showLeft: true,
    showMiddle: true,
    showRight: false
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // State to hold the profile data, allowing updates from graph expansion
  const [profileData, setProfileData] = useState<Profile | null>(companyAssessmentData || null);
  const [loadedAssessment, setLoadedAssessment] = useState(companyAssessmentData);
  const [monitoringDetail, setMonitoringDetail] = useState<GraphMonitoringEntityDetail | null>(null);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringSaving, setMonitoringSaving] = useState(false);
  const [monitoringError, setMonitoringError] = useState<string | null>(null);

  // Reset before rendering children. An effect lets the graph mount with the
  // previous expanded profile after navigation, mistaking its partial node
  // positions for a saved layout and disabling stabilization for the new result.
  if (loadedAssessment !== companyAssessmentData) {
    setLoadedAssessment(companyAssessmentData);
    setProfileData(companyAssessmentData || null);
    setSelectedDate(null);
    setGeographicArea(null);
    setHiddenNodeIds(new Set());
  }

  const geographicCompanyIds = useMemo(() => geographicArea && profileData?.graphData
    ? getCompaniesInArea(profileData.graphData.nodes, profileData.graphData.relationships, geographicArea)
    : null, [geographicArea, profileData]);

  const loadMonitoring = useCallback(async (orgNumber: string) => {
    setMonitoringLoading(true);
    setMonitoringError(null);

    try {
      const nextDetail = await fetchGraphMonitoringDetail(orgNumber, {
        snapshotLimit: 1,
        diffLimit: 5,
      });
      setMonitoringDetail(nextDetail);
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 404) {
        setMonitoringDetail(null);
      } else {
        setMonitoringError(getMonitoringErrorMessage(requestError));
      }
    } finally {
      setMonitoringLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!profileData?.orgNumber) {
      setMonitoringDetail(null);
      setMonitoringError(null);
      setMonitoringLoading(false);
      return;
    }

    loadMonitoring(profileData.orgNumber);
  }, [loadMonitoring, profileData?.orgNumber]);

  const handleEnableMonitoring = useCallback(async () => {
    if (!profileData?.orgNumber) {
      return;
    }

    setMonitoringSaving(true);
    setMonitoringError(null);

    try {
      const nextDetail = await enableGraphMonitoring(
        profileData.orgNumber,
        profileData.orgName,
      );
      setMonitoringDetail(nextDetail);
      toast.success(t('monitoring.overview.bulkEnabled', { count: 1 }));
    } catch (toggleError) {
      const message = getMonitoringErrorMessage(toggleError);
      setMonitoringError(message);
      toast.error(message);
    } finally {
      setMonitoringSaving(false);
    }
  }, [profileData?.orgName, profileData?.orgNumber, t, toast]);

  const handleDisableMonitoring = useCallback(async () => {
    if (!profileData?.orgNumber) {
      return;
    }

    setMonitoringSaving(true);
    setMonitoringError(null);

    try {
      await unsubscribeGraphMonitoring(profileData.orgNumber);
      setMonitoringDetail((currentDetail) =>
        currentDetail
          ? {
              ...currentDetail,
              monitoredEntity: {
                ...currentDetail.monitoredEntity,
                enabled: false,
              },
            }
          : currentDetail,
      );
      toast.success(t('monitoring.overview.removedFromMonitoring', { name: profileData.orgName || profileData.orgNumber }));
    } catch (toggleError) {
      const message = getMonitoringErrorMessage(toggleError);
      setMonitoringError(message);
      toast.error(message);
    } finally {
      setMonitoringSaving(false);
    }
  }, [profileData?.orgName, profileData?.orgNumber, t, toast]);

  const isMonitored = monitoringDetail?.monitoredEntity.enabled ?? false;

  // Handler for graph updates (node expansion)
  const handleGraphUpdate = (newNodes: any[], newEdges: any[]) => {
    setProfileData(prev => {
      if (!prev) return prev;
      
      // Create a new profile object with updated graphData
      return {
        ...prev,
        graphData: {
          ...prev.graphData,
          nodes: [...prev.graphData.nodes, ...newNodes],
          relationships: [...prev.graphData.relationships, ...newEdges]
        }
      };
    });
  };

  // Store contextId in sessionStorage when component mounts (client-side only)
  useEffect(() => {
    if (companyAssessmentData?.contextId && typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.setItem('contextId', companyAssessmentData.contextId);
      } catch (error) {}
    }
  }, [companyAssessmentData]);

  const handleMouseDown = (e: React.MouseEvent, resizer: 'left' | 'right') => {
    e.preventDefault();
    const startX = e.clientX;
    const startLeft = layout.left;
    const startMiddle = layout.middle;
    const startRight = layout.right;
    const containerWidth = containerRef.current?.offsetWidth || 1000;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaPercent = (deltaX / containerWidth) * 100;

      if (resizer === 'left') {
        // Resizing between Left and Middle
        let newLeft = startLeft + deltaPercent;
        let newMiddle = startMiddle - deltaPercent;

        // Constraints
        if (newLeft < 10) { newLeft = 10; newMiddle = startLeft + startMiddle - 10; }
        if (newMiddle < 10) { newMiddle = 10; newLeft = startLeft + startMiddle - 10; }

        setLayout(prev => ({ ...prev, left: newLeft, middle: newMiddle }));
      } else {
        // Resizing between Middle and Right
        let newMiddle = startMiddle + deltaPercent;
        let newRight = startRight - deltaPercent;

        // Constraints
        if (newRight < 10) { newRight = 10; newMiddle = startMiddle + startRight - 10; }
        if (newMiddle < 10) { newMiddle = 10; newRight = startMiddle + startRight - 10; }

        setLayout(prev => ({ ...prev, middle: newMiddle, right: newRight }));
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleToggle = useCallback((panel: PanelKey) => {
      setLayout(prev => {
          const isOpening = (panel === 'left' && !prev.showLeft) || (panel === 'middle' && !prev.showMiddle) || (panel === 'right' && !prev.showRight);
          
          if (isOpening) {
              // Opening
              let newLeft = prev.left;
              let newMiddle = prev.middle;
              let newRight = prev.right;
              
              if (panel === 'left') {
                  newLeft = 25;
                  if (prev.showMiddle && prev.showRight) { newMiddle = 50; newRight = 25; }
                  else if (prev.showMiddle) { newMiddle = 75; }
                  else if (prev.showRight) { newRight = 75; }
                  else { newLeft = 100; }
              } else if (panel === 'middle') {
                  newMiddle = 50;
                  if (prev.showLeft && prev.showRight) { newLeft = 25; newRight = 25; }
                  else if (prev.showLeft) { newLeft = 50; }
                  else if (prev.showRight) { newRight = 50; }
                  else { newMiddle = 100; }
              } else if (panel === 'right') {
                  newRight = 25;
                  if (prev.showLeft && prev.showMiddle) { newLeft = 25; newMiddle = 50; }
                  else if (prev.showLeft) { newLeft = 75; }
                  else if (prev.showMiddle) { newMiddle = 75; }
                  else { newRight = 100; }
              }
              
              return { ...prev, left: newLeft, middle: newMiddle, right: newRight, [panel === 'left' ? 'showLeft' : panel === 'middle' ? 'showMiddle' : 'showRight']: true };
          } else {
              // Closing
              let newLeft = prev.left;
              let newMiddle = prev.middle;
              let newRight = prev.right;
              
              if (panel === 'left') {
                  if (prev.showMiddle) newMiddle += prev.left;
                  else if (prev.showRight) newRight += prev.left;
                  newLeft = 0;
              } else if (panel === 'middle') {
                  if (prev.showLeft && prev.showRight) { newLeft += prev.middle / 2; newRight += prev.middle / 2; }
                  else if (prev.showLeft) newLeft += prev.middle;
                  else if (prev.showRight) newRight += prev.middle;
                  newMiddle = 0;
              } else if (panel === 'right') {
                  if (prev.showMiddle) newMiddle += prev.right;
                  else if (prev.showLeft) newLeft += prev.right;
                  newRight = 0;
              }
              
              return { ...prev, left: newLeft, middle: newMiddle, right: newRight, [panel === 'left' ? 'showLeft' : panel === 'middle' ? 'showMiddle' : 'showRight']: false };
          }
      });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || isEditableTarget(event.target)) {
        return;
      }

      let panel: PanelKey | null = null;

      if (event.key === '1') {
        panel = 'left';
      } else if (event.key === '2') {
        panel = 'middle';
      } else if (event.key === '3') {
        panel = 'right';
      }

      if (!panel) {
        return;
      }

      event.preventDefault();
      handleToggle(panel);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleToggle]);

  if (httpStatus === 400) {
    return <Typography variant="h6">{errorMessage}</Typography>;
  }

  if (httpStatus === 404) {
    return <Typography variant="h6">{t('assessment.details.notFoundForOrgNumber')}</Typography>;
  }

  if (httpStatus === 429) {
    return <Typography variant="h6">{t('assessment.details.quotaExceeded')}</Typography>;
  }

  if (navigation.state === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'column' }}>
        <CircularProgress size={36} />
        <Box sx={{ marginTop: 2, fontSize: '1.2rem', color: 'gray' }}>
          {t('common.states.loading')}
        </Box>
      </Box>
    );
  }

  if (!profileData) {
    return null;
  }

  const tenantId = getTenantIdFromAccessToken(auth.user?.access_token);
  const canShowIvoLicenses = isIvoLicensesTenant(tenantId);
  const canShowCarEntityList = isCarEntityListTenant(tenantId);
  const canShowTenantCases = isTenantCaseTenant(tenantId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
       {geographicCompanyIds && <Alert severity={geographicCompanyIds.size ? 'info' : 'warning'}
         action={<Button color="inherit" size="small" onClick={() => setGeographicArea(null)}>{t('ui.map.clearArea')}</Button>}>
         {t('ui.map.areaResult', { count: geographicCompanyIds.size })} {t('ui.map.areaScope')}
       </Alert>}
       <Box ref={containerRef} sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {layout.showLeft && (
             <Box sx={{ width: `${layout.left}%`, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                <CompanyDetailsPanel
                    profile={profileData}
                    canShowIvoLicenses={canShowIvoLicenses}
                    monitoringDetail={monitoringDetail}
                    monitoringError={monitoringError}
                    monitoringLoading={monitoringLoading}
                    monitoringSaving={monitoringSaving}
                    isMonitored={isMonitored}
                    onEnableMonitoring={handleEnableMonitoring}
                    onDisableMonitoring={handleDisableMonitoring}
                    onClose={() => handleToggle('left')}
                />
             </Box>
          )}
          
          {layout.showLeft && (layout.showMiddle || layout.showRight) && (
             <Resizer onMouseDown={(e) => handleMouseDown(e, 'left')} />
          )}
          
          <Box sx={{ width: `${layout.middle}%`, display: layout.showMiddle ? 'flex' : 'none', flexDirection: 'column' }}>
            <OrgGraphPanel 
                profile={profileData} 
                enableIvoLicenseOverlay={canShowIvoLicenses}
                enableCarEntityList={canShowCarEntityList}
                enableVehicleExpansion={canShowCarEntityList}
                enableTenantCases={canShowTenantCases}
                onHiddenNodesChange={setHiddenNodeIds} 
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onGraphUpdate={handleGraphUpdate}
                geographicCompanyIds={geographicCompanyIds}
                onMonitoringStateChanged={() => {
                  if (profileData.orgNumber) {
                    loadMonitoring(profileData.orgNumber);
                  }
                }}
                onClose={() => handleToggle('middle')}
            />
          </Box>
          
          {layout.showMiddle && layout.showRight && (
             <Resizer onMouseDown={(e) => handleMouseDown(e, 'right')} />
          )}

          {layout.showRight && (
             <Box sx={{ width: `${layout.right}%`, borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <CompanyMapPanel 
                    profile={profileData} 
                    hiddenNodeIds={hiddenNodeIds} 
                    selectedDate={selectedDate} 
                    onDateChange={setSelectedDate}
                    geographicArea={geographicArea}
                    geographicCompanyIds={geographicCompanyIds}
                    onGeographicAreaChange={setGeographicArea}
                    onClose={() => handleToggle('right')} 
                />
             </Box>
          )}
       </Box>
       
       {/* Toolbar to reopen panels */}
       <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center', bgcolor: 'background.paper' }}>
          <ButtonGroup variant="text" size="small" sx={{
            bgcolor: 'background.default', p: 0.5, borderRadius: 2,
            '& .MuiButtonGroup-grouped': { border: 0, borderRadius: 1, mx: 0.25 },
            '& .MuiButton-root[aria-pressed="true"]': { bgcolor: 'action.selected', color: 'primary.main' },
          }}>
             <Button 
                aria-pressed={layout.showLeft}
                onClick={() => handleToggle('left')}
                color={layout.showLeft ? "primary" : "inherit"}
                title={t('assessment.details.toggleDetails')}
                aria-keyshortcuts="1"
             >
                {t('assessment.details.panelTitle')}
             </Button>
             <Button 
                aria-pressed={layout.showMiddle}
                onClick={() => handleToggle('middle')}
                color={layout.showMiddle ? "primary" : "inherit"}
                title={t('assessment.details.toggleGraph')}
                aria-keyshortcuts="2"
             >
                {t('assessment.details.graph')}
             </Button>
             <Button 
                aria-pressed={layout.showRight}
                onClick={() => handleToggle('right')}
                color={layout.showRight ? "primary" : "inherit"}
                title={t('assessment.details.toggleMap')}
                aria-keyshortcuts="3"
             >
                {t('assessment.details.map')}
             </Button>
          </ButtonGroup>
       </Box>
    </Box>
  );
}
